import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import toast from 'react-hot-toast';
import { getBlogPost, updateBlogPost, regenerateBlogImage } from '@/services/blogApi';
import type { BlogPost, BlogStatus } from '@/types/blog';
import { formatDate } from '@/utils/formatters';

const STATUS_BADGES: Record<BlogStatus, { variant: 'gray' | 'info' | 'success' | 'warning' | 'danger'; label: string }> = {
  draft: { variant: 'gray', label: 'Draft' },
  in_review: { variant: 'warning', label: 'In Review' },
  published: { variant: 'success', label: 'Published' },
  archived: { variant: 'info', label: 'Archived' },
};

// Dark theme styles for blog content (matching the public site)
const darkProseStyles = `
  .blog-preview-content {
    color: #e5e7eb;
    font-family: Inter, system-ui, sans-serif;
    line-height: 1.8;
  }
  .blog-preview-content h2 {
    color: #f9fafb;
    font-size: 1.75rem;
    font-weight: 700;
    margin-top: 2.5rem;
    margin-bottom: 1rem;
    line-height: 1.3;
  }
  .blog-preview-content h3 {
    color: #f9fafb;
    font-size: 1.375rem;
    font-weight: 600;
    margin-top: 2rem;
    margin-bottom: 0.75rem;
    line-height: 1.4;
  }
  .blog-preview-content h4 {
    color: #f3f4f6;
    font-size: 1.125rem;
    font-weight: 600;
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
  }
  .blog-preview-content p {
    margin-bottom: 1.25rem;
    color: #d1d5db;
  }
  .blog-preview-content a {
    color: #60a5fa;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .blog-preview-content a:hover {
    color: #93c5fd;
  }
  .blog-preview-content strong, .blog-preview-content b {
    color: #f9fafb;
    font-weight: 600;
  }
  .blog-preview-content ul, .blog-preview-content ol {
    margin-bottom: 1.25rem;
    padding-left: 1.5rem;
  }
  .blog-preview-content li {
    margin-bottom: 0.5rem;
    color: #d1d5db;
  }
  .blog-preview-content ul li {
    list-style-type: disc;
  }
  .blog-preview-content ol li {
    list-style-type: decimal;
  }
  .blog-preview-content blockquote {
    border-left: 4px solid #3b82f6;
    padding-left: 1rem;
    margin: 1.5rem 0;
    font-style: italic;
    color: #9ca3af;
    background-color: rgba(59, 130, 246, 0.1);
    padding: 1rem;
    border-radius: 0 0.5rem 0.5rem 0;
  }
  .blog-preview-content code {
    background-color: #374151;
    color: #fbbf24;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    font-size: 0.875em;
    font-family: ui-monospace, monospace;
  }
  .blog-preview-content pre {
    background-color: #1f2937;
    padding: 1rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    margin: 1.5rem 0;
  }
  .blog-preview-content pre code {
    background: none;
    padding: 0;
    color: #e5e7eb;
  }
  .blog-preview-content hr {
    border-color: #374151;
    margin: 2rem 0;
  }
  .blog-preview-content table {
    width: 100%;
    border-collapse: collapse;
    margin: 1.5rem 0;
  }
  .blog-preview-content th, .blog-preview-content td {
    border: 1px solid #374151;
    padding: 0.75rem;
    text-align: left;
  }
  .blog-preview-content th {
    background-color: #1f2937;
    color: #f9fafb;
    font-weight: 600;
  }
`;

// Section parser functions (matching the public site)
interface ParsedSection {
  heading: string;
  content: string;
  originalIndex: number;
}

function parseHtmlIntoSections(html: string): ParsedSection[] {
  if (!html) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const container = doc.body.firstChild as HTMLElement;

  const sections: ParsedSection[] = [];
  let currentSection: ParsedSection | null = null;
  let sectionIndex = 0;

  // Helper to create HTML string from nodes
  const getOuterHtml = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || '';
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      return (node as Element).outerHTML;
    }
    return '';
  };

  // Process all child nodes
  container.childNodes.forEach((node) => {
    const isH2 = node.nodeType === Node.ELEMENT_NODE &&
      (node as Element).tagName?.toUpperCase() === 'H2';

    if (isH2) {
      // Save previous section
      if (currentSection) {
        sections.push(currentSection);
      }
      // Start new section
      currentSection = {
        heading: (node as Element).textContent || '',
        content: getOuterHtml(node),
        originalIndex: sectionIndex++,
      };
    } else {
      // Add content to current section
      if (currentSection) {
        currentSection.content += getOuterHtml(node);
      } else {
        // Content before first h2 - create intro section
        const nodeHtml = getOuterHtml(node);
        if (nodeHtml.trim()) {
          currentSection = {
            heading: 'Introduction',
            content: nodeHtml,
            originalIndex: sectionIndex++,
          };
        }
      }
    }
  });

  // Don't forget the last section
  if (currentSection) {
    sections.push(currentSection);
  }

  return sections;
}

function reconstructHtmlFromSections(sections: ParsedSection[], updates: Map<number, string>): string {
  return sections.map((section) => {
    const updatedContent = updates.get(section.originalIndex);
    return updatedContent !== undefined ? updatedContent : section.content;
  }).join('\n');
}

// Editable Section Component
interface EditableSectionProps {
  section: ParsedSection;
  isEditing: boolean;
  onEdit: () => void;
  onSave: (content: string) => void;
  onCancel: () => void;
}

function EditableSection({ section, isEditing, onEdit, onSave, onCancel }: EditableSectionProps) {
  const [editContent, setEditContent] = useState(section.content);
  const [viewMode, setViewMode] = useState<'visual' | 'html'>('visual');

  useEffect(() => {
    setEditContent(section.content);
  }, [section.content]);

  if (isEditing) {
    return (
      <div className="relative border-2 border-blue-500 rounded-lg p-4 my-4 bg-gray-900">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-blue-400">
              Editing: {section.heading}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(viewMode === 'visual' ? 'html' : 'visual')}
              className="px-3 py-1 text-xs font-medium rounded bg-gray-700 text-gray-300 hover:bg-gray-600"
            >
              {viewMode === 'visual' ? 'HTML' : 'Visual'}
            </button>
            <button
              onClick={onCancel}
              className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => onSave(editContent)}
              className="p-1.5 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {viewMode === 'html' ? (
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full min-h-[300px] p-4 font-mono text-sm bg-gray-800 text-gray-200 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            spellCheck={false}
            aria-label="Section HTML content"
          />
        ) : (
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => setEditContent(e.currentTarget.innerHTML)}
            dangerouslySetInnerHTML={{ __html: editContent }}
            className="w-full min-h-[200px] p-4 bg-gray-800 text-gray-200 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 blog-preview-content"
          />
        )}

        <div className="flex justify-end gap-2 mt-3">
          <Button size="sm" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => onSave(editContent)}>
            Save Section
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onEdit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEdit();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Edit section content"
      className="relative group cursor-pointer rounded-lg transition-all hover:ring-2 hover:ring-blue-400 hover:ring-dashed p-2 -mx-2"
    >
      <div className="absolute -top-3 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="px-2 py-1 text-xs font-medium bg-blue-500 text-white rounded">
          Click to edit
        </span>
      </div>
      <div
        className="blog-preview-content"
        dangerouslySetInnerHTML={{ __html: section.content }}
      />
    </div>
  );
}

// Editable Featured Image Component
interface EditableFeaturedImageProps {
  imageUrl: string;
  imageAlt: string;
  postId: string;
  postTitle: string;
  onImageChange: (url: string, alt: string) => void;
}

function EditableFeaturedImage({ imageUrl, imageAlt, postId, postTitle, onImageChange }: EditableFeaturedImageProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [pendingImage, setPendingImage] = useState<{ url: string; alt: string } | null>(null);

  const displayUrl = pendingImage?.url || imageUrl;
  const displayAlt = pendingImage?.alt || imageAlt;

  const handleRegenerate = async () => {
    setIsGenerating(true);
    try {
      const result = await regenerateBlogImage(postId, `Create a photorealistic image for: ${postTitle}`, imageAlt);
      if (result.success && result.image_url) {
        setPendingImage({ url: result.image_url, alt: result.image_alt || imageAlt });
        toast.success('New image generated! Accept or discard.');
      } else {
        throw new Error(result.message || 'Image generation failed');
      }
    } catch (error) {
      console.error('Image regeneration failed:', error);
      toast.error('Failed to regenerate image');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAccept = () => {
    if (pendingImage) {
      onImageChange(pendingImage.url, pendingImage.alt);
      setPendingImage(null);
      toast.success('New image accepted');
    }
  };

  const handleDiscard = () => {
    setPendingImage(null);
    toast.info('Reverted to original image');
  };

  return (
    <div
      className="relative mb-8 rounded-lg overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <img
        src={displayUrl}
        alt={displayAlt}
        className={`w-full h-80 object-cover transition-opacity ${isGenerating ? 'opacity-50' : ''}`}
      />

      {/* Controls overlay */}
      <div
        className={`absolute top-4 right-4 flex gap-2 transition-opacity ${
          isHovered || isGenerating || pendingImage ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {pendingImage ? (
          <>
            <Button
              size="sm"
              onClick={handleAccept}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckIcon className="w-4 h-4 mr-1" />
              Accept
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={handleDiscard}
            >
              <XMarkIcon className="w-4 h-4 mr-1" />
              Discard
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            onClick={handleRegenerate}
            disabled={isGenerating}
          >
            <ArrowPathIcon className={`w-4 h-4 mr-1 ${isGenerating ? 'animate-spin' : ''}`} />
            {isGenerating ? 'Generating...' : 'Regenerate Image'}
          </Button>
        )}
      </div>

      {/* Loading overlay */}
      {isGenerating && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60">
          <ArrowPathIcon className="w-8 h-8 text-white animate-spin" />
          <p className="text-white mt-3 font-medium">Generating new image...</p>
          <p className="text-gray-300 text-sm mt-1">This may take 20-30 seconds</p>
        </div>
      )}

      {/* Pending image indicator */}
      {pendingImage && !isGenerating && (
        <div className="absolute bottom-4 left-4 right-4 bg-black/80 rounded-lg p-3">
          <p className="text-amber-400 text-sm font-medium text-center">
            New image generated - Accept to save or Discard to keep original
          </p>
        </div>
      )}
    </div>
  );
}

// Editable Answer Capsule Component
interface EditableAnswerCapsuleProps {
  value: string;
  onChange: (value: string) => void;
}

function EditableAnswerCapsule({ value, onChange }: EditableAnswerCapsuleProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  if (isEditing) {
    return (
      <div className="mb-8 bg-blue-900/30 border-l-4 border-blue-500 rounded-r-lg p-6 ring-2 ring-blue-500">
        <p className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-2">
          Key Takeaway
        </p>
        <textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="w-full min-h-[100px] p-3 bg-gray-800 text-gray-200 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Key takeaway text"
        />
        <div className="flex justify-end gap-2 mt-3">
          <Button size="sm" variant="secondary" onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => { onChange(editValue); setIsEditing(false); }}>
            Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsEditing(true);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Edit key takeaway"
      className="mb-8 bg-blue-900/30 border-l-4 border-blue-500 rounded-r-lg p-6 cursor-pointer hover:ring-2 hover:ring-blue-400 hover:ring-dashed transition-all group relative"
    >
      <div className="absolute -top-3 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="px-2 py-1 text-xs font-medium bg-blue-500 text-white rounded">
          Click to edit
        </span>
      </div>
      <p className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-2">
        Key Takeaway
      </p>
      <p className="text-lg text-gray-200 leading-relaxed">{value}</p>
    </div>
  );
}

// Main Preview Page Component
export default function BlogPostPreview() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  const [sectionUpdates, setSectionUpdates] = useState<Map<number, string>>(new Map());
  const [pendingUpdates, setPendingUpdates] = useState<Partial<BlogPost>>({});

  // Fetch post
  const { data: post, isLoading } = useQuery<BlogPost | null>({
    queryKey: ['blogPost', postId],
    queryFn: () => getBlogPost(postId!),
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (updates: Partial<BlogPost>) => {
      return updateBlogPost(postId!, updates);
    },
    onSuccess: () => {
      toast.success('Changes saved successfully');
      queryClient.invalidateQueries({ queryKey: ['blogPost', postId] });
      setHasUnsavedChanges(false);
      setSectionUpdates(new Map());
      setPendingUpdates({});
      setIsEditMode(false);
    },
    onError: () => toast.error('Failed to save changes'),
  });

  // Parse sections
  const sections = useMemo(() => {
    if (!post?.content) return [];
    return parseHtmlIntoSections(post.content);
  }, [post?.content]);

  // Get current values
  const currentContent = pendingUpdates.content || post?.content || '';
  const currentAnswerCapsule = pendingUpdates.answer_capsule ?? post?.answer_capsule ?? '';
  const currentImageUrl = pendingUpdates.featured_image_url || post?.featured_image_url || '';
  const currentImageAlt = pendingUpdates.featured_image_alt || post?.featured_image_alt || '';

  // Handle section save
  const handleSectionSave = useCallback((index: number, newContent: string) => {
    const newUpdates = new Map(sectionUpdates);
    newUpdates.set(index, newContent);
    setSectionUpdates(newUpdates);

    const updatedHtml = reconstructHtmlFromSections(sections, newUpdates);
    setPendingUpdates(prev => ({ ...prev, content: updatedHtml }));
    setHasUnsavedChanges(true);
    setEditingSectionIndex(null);
  }, [sections, sectionUpdates]);

  // Handle answer capsule change
  const handleAnswerCapsuleChange = useCallback((value: string) => {
    setPendingUpdates(prev => ({ ...prev, answer_capsule: value }));
    setHasUnsavedChanges(true);
  }, []);

  // Handle image change
  const handleImageChange = useCallback((url: string, alt: string) => {
    setPendingUpdates(prev => ({ ...prev, featured_image_url: url, featured_image_alt: alt }));
    setHasUnsavedChanges(true);
  }, []);

  // Handle save all changes
  const handleSave = async () => {
    if (Object.keys(pendingUpdates).length === 0 && sectionUpdates.size === 0) {
      setIsEditMode(false);
      return;
    }

    saveMutation.mutate(pendingUpdates);
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to discard them?')) {
        return;
      }
    }
    setSectionUpdates(new Map());
    setPendingUpdates({});
    setHasUnsavedChanges(false);
    setIsEditMode(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Blog post not found</p>
        <Button variant="secondary" onClick={() => navigate('/blog')} className="mt-4">
          Back to Blog Posts
        </Button>
      </div>
    );
  }

  // Get author info (if embedded in post)
  const author = (post as any).blog_author;

  return (
    <div className="min-h-screen bg-gray-900">
      <style>{darkProseStyles}</style>

      {/* Header Bar */}
      <div className="sticky top-0 z-50 bg-gray-800 border-b border-gray-700 px-6 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/blog')}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-white truncate max-w-md">
                {post.title}
              </h1>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant={STATUS_BADGES[post.status].variant} dot>
                  {STATUS_BADGES[post.status].label}
                </Badge>
                <span className="text-xs text-gray-400">Preview Mode</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {hasUnsavedChanges && (
              <span className="text-sm text-amber-400">Unsaved changes</span>
            )}

            {isEditMode ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCancel}
                  disabled={saveMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  loading={saveMutation.isPending}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/blog/${post.id}`)}
                >
                  <PencilIcon className="w-4 h-4 mr-1" />
                  Edit in Form
                </Button>
                <Button
                  size="sm"
                  onClick={() => setIsEditMode(true)}
                >
                  <EyeIcon className="w-4 h-4 mr-1" />
                  Edit in Preview
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Edit Mode Banner */}
      {isEditMode && (
        <div className="bg-blue-600 text-white text-center py-2 px-4 text-sm">
          <strong>EDIT MODE</strong> - Click on any section to edit. Changes will be saved when you click "Save Changes".
        </div>
      )}

      {/* Blog Content Preview */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Category & Meta */}
        <div className="mb-6">
          <span className="text-blue-400 text-sm font-medium uppercase tracking-wider">
            {post.category_slug?.replace(/-/g, ' ') || 'Uncategorized'}
            {post.subcategory && ` / ${post.subcategory}`}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-4xl font-bold text-white mb-6 leading-tight">
          {post.title}
        </h1>

        {/* Author & Meta */}
        <div className="flex items-center gap-6 mb-8 text-gray-400 text-sm">
          {author && (
            <div className="flex items-center gap-2">
              {author.avatar_url ? (
                <img src={author.avatar_url} alt={author.name} className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-700" />
              )}
              <span>{author.name}</span>
            </div>
          )}
          <span>{post.published_at ? formatDate(post.published_at) : 'Draft'}</span>
          <span>{post.reading_time_minutes || 0} min read</span>
          <span>{(post.view_count || 0).toLocaleString()} views</span>
        </div>

        {/* Featured Image */}
        {currentImageUrl && (
          isEditMode ? (
            <EditableFeaturedImage
              imageUrl={currentImageUrl}
              imageAlt={currentImageAlt || post.title}
              postId={post.id}
              postTitle={post.title}
              onImageChange={handleImageChange}
            />
          ) : (
            <img
              src={currentImageUrl}
              alt={currentImageAlt || post.title}
              className="w-full h-80 object-cover rounded-lg mb-8"
            />
          )
        )}

        {/* Answer Capsule */}
        {(currentAnswerCapsule || isEditMode) && (
          isEditMode ? (
            <EditableAnswerCapsule
              value={currentAnswerCapsule || ''}
              onChange={handleAnswerCapsuleChange}
            />
          ) : currentAnswerCapsule ? (
            <div className="mb-8 bg-blue-900/30 border-l-4 border-blue-500 rounded-r-lg p-6">
              <p className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-2">
                Key Takeaway
              </p>
              <p className="text-lg text-gray-200 leading-relaxed">{currentAnswerCapsule}</p>
            </div>
          ) : null
        )}

        {/* Content */}
        {isEditMode && sections.length > 0 ? (
          <div>
            {sections.map((section, index) => {
              const currentSectionContent = sectionUpdates.get(index) || section.content;
              const currentSection = { ...section, content: currentSectionContent };

              return (
                <EditableSection
                  key={`section-${index}`}
                  section={currentSection}
                  isEditing={editingSectionIndex === index}
                  onEdit={() => setEditingSectionIndex(index)}
                  onSave={(content) => handleSectionSave(index, content)}
                  onCancel={() => setEditingSectionIndex(null)}
                />
              );
            })}
          </div>
        ) : (
          <div
            className="blog-preview-content"
            dangerouslySetInnerHTML={{ __html: currentContent }}
          />
        )}

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-gray-700">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-gray-800 text-gray-400 text-sm rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Author Bio */}
        {author && (
          <div className="mt-12 pt-8 border-t border-gray-700">
            <div className="flex items-start gap-4">
              {author.avatar_url ? (
                <img src={author.avatar_url} alt={author.name} className="w-16 h-16 rounded-full" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-700" />
              )}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">About the Author</p>
                <p className="text-lg font-semibold text-white">{author.name}</p>
                <p className="text-gray-400 mt-1">{author.bio}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
